import { useEffect, useState } from 'react';
import api from '../services/ApiService';

type Commit = { sha: string; author: string; date: string; subject: string };
type Preview = {
  pending_count: number; commits: Commit[]; diffstat: string;
  twofa_enabled: boolean; prod_configured: boolean; history: any[];
};

const C = {
  card: { background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, padding: 20, marginBottom: 20 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 18, letterSpacing: 4, textAlign: 'center' as const,
           background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.4)', color: '#f1f5f9', outline: 'none', boxSizing: 'border-box' as const },
  mono: { fontFamily: 'ui-monospace, monospace', fontSize: 12 },
};

export default function DeployToProductionPage({ user }: any = {}) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);
  const [pv, setPv]         = useState<Preview | null>(null);
  const [loading, setLoad]  = useState(true);
  const [msg, setMsg]       = useState<{ text: string; ok: boolean } | null>(null);

  // enrollment
  const [enroll, setEnroll] = useState<{ secret: string; qr_svg: string; otpauth_uri: string } | null>(null);
  const [enrollCode, setEnrollCode] = useState('');

  // deploy
  const [showGate, setShowGate] = useState<null | { dryRun: boolean }>(null);
  const [deployCode, setDeployCode] = useState('');
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [result, setResult] = useState<null | { status: string; dry_run: boolean }>(null);

  function flash(text: string, ok = true) { setMsg({ text, ok }); setTimeout(() => setMsg(null), 5000); }

  async function load() {
    setLoad(true);
    try { setPv(await api.deployPreview()); } catch (e: any) { flash(e?.response?.data?.error || 'Failed to load deploy status', false); }
    setLoad(false);
  }
  useEffect(() => { if (isSuperAdmin) load(); /* eslint-disable-next-line */ }, []);

  async function startEnroll() {
    try { setEnroll(await api.setup2fa()); setEnrollCode(''); }
    catch (e: any) { flash(e?.response?.data?.error || 'Setup failed', false); }
  }
  async function confirmEnroll() {
    try { await api.verify2fa(enrollCode); setEnroll(null); flash('2FA enabled — you can now deploy'); load(); }
    catch (e: any) { flash(e?.response?.data?.error || 'Invalid code', false); }
  }

  async function runDeploy() {
    if (!showGate) return;
    setRunning(true); setLog([]); setResult(null);
    try {
      const r = await api.deployExecute(deployCode, showGate.dryRun);
      setLog(r.log || []);
      setResult({ status: r.status || (r.success ? 'success' : 'failed'), dry_run: !!r.dry_run });
      flash(showGate.dryRun ? 'Dry-run complete' : `Deploy ${r.status}`, r.status === 'success');
      setShowGate(null); setDeployCode(''); load();
    } catch (e: any) {
      const d = e?.response?.data;
      setLog(d?.log || []);
      setResult({ status: d?.status || 'failed', dry_run: !!showGate.dryRun });
      flash(d?.error || 'Deploy failed', false);
    }
    setRunning(false);
  }

  if (!isSuperAdmin) return <div style={{ padding: 24, color: '#94a3b8' }}>Super Admin only.</div>;

  return (
    <div style={{ padding: '20px 24px', maxWidth: 940 }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', marginBottom: 4 }}>Deploy to Production</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
        Promote reviewed changes from <b style={{ color: '#c4b5fd' }}>develop</b> (this staging server) to
        <b style={{ color: '#f59e0b' }}> production</b> (TECDUCC). Gated by 2FA. Only code is promoted — production data & config are untouched.
      </div>

      {msg && (
        <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 8,
          background: msg.ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${msg.ok ? '#10b981' : '#ef4444'}40`, color: msg.ok ? '#6ee7b7' : '#fca5a5', fontSize: 13 }}>
          {msg.text}
        </div>
      )}

      {loading ? <div style={{ color: '#94a3b8', padding: 20 }}>Loading…</div> : pv && (
        <>
          {/* ── 2FA status / enrollment ─────────────────────────────── */}
          {!pv.twofa_enabled && (
            <div style={C.card}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#fca5a5', marginBottom: 8 }}>🔐 Two-factor authentication required</div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 14 }}>
                Deploying to production requires TOTP 2FA. Enrol an authenticator app (Google Authenticator, Authy, 1Password…).
              </div>
              {!enroll ? (
                <button className="admin-btn primary" onClick={startEnroll}>Set up 2FA</button>
              ) : (
                <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ background: '#fff', padding: 8, borderRadius: 8, width: 210, height: 210 }}
                       dangerouslySetInnerHTML={{ __html: enroll.qr_svg }} />
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Scan the QR, or enter this secret manually:</div>
                    <div style={{ ...C.mono, color: '#c4b5fd', background: 'rgba(15,23,42,0.6)', padding: '8px 10px', borderRadius: 6, wordBreak: 'break-all', marginBottom: 14 }}>
                      {enroll.secret}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Enter the 6-digit code to confirm:</div>
                    <input style={C.input} maxLength={6} placeholder="000000" value={enrollCode}
                      onChange={e => setEnrollCode(e.target.value.replace(/\D/g, ''))} />
                    <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                      <button className="admin-btn primary" onClick={confirmEnroll} disabled={enrollCode.length !== 6}>Enable 2FA</button>
                      <button className="admin-btn" onClick={() => setEnroll(null)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Pending changes ─────────────────────────────────────── */}
          <div style={C.card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>
                Pending promotion <span style={{ color: '#94a3b8', fontWeight: 400 }}>· {pv.pending_count} commit(s)</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, fontWeight: 700,
                  background: pv.twofa_enabled ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                  color: pv.twofa_enabled ? '#6ee7b7' : '#fca5a5' }}>
                  2FA {pv.twofa_enabled ? 'ON' : 'OFF'}
                </span>
                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, fontWeight: 700,
                  background: pv.prod_configured ? 'rgba(59,130,246,0.15)' : 'rgba(100,116,139,0.15)',
                  color: pv.prod_configured ? '#93c5fd' : '#94a3b8' }}
                  title={pv.prod_configured ? 'Production target wired' : 'Production not wired yet — real deploys run as dry-run'}>
                  PROD {pv.prod_configured ? 'WIRED' : 'NOT WIRED'}
                </span>
                <button className="admin-btn sm" onClick={load}>⟳ Refresh</button>
              </div>
            </div>

            {pv.pending_count === 0 ? (
              <div style={{ color: '#64748b', fontSize: 13, padding: '8px 0' }}>✓ Production is up to date with develop — nothing to promote.</div>
            ) : (
              <>
                <table className="admin-table" style={{ marginBottom: 12 }}>
                  <thead><tr><th>Commit</th><th>Date</th><th>Author</th><th>Change</th></tr></thead>
                  <tbody>
                    {pv.commits.map(c => (
                      <tr key={c.sha}>
                        <td style={{ ...C.mono, color: '#c4b5fd' }}>{c.sha}</td>
                        <td style={{ color: '#94a3b8', fontSize: 12 }}>{c.date}</td>
                        <td style={{ color: '#94a3b8', fontSize: 12 }}>{c.author}</td>
                        <td style={{ color: '#e2e8f0', fontSize: 13 }}>{c.subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pv.diffstat && (
                  <pre style={{ ...C.mono, color: '#64748b', background: 'rgba(2,6,23,0.6)', padding: 12, borderRadius: 8,
                    maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{pv.diffstat}</pre>
                )}
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button className="admin-btn" disabled={!pv.twofa_enabled}
                    onClick={() => { setShowGate({ dryRun: true }); setDeployCode(''); }}>
                    ▶ Dry run
                  </button>
                  <button className="admin-btn primary" disabled={!pv.twofa_enabled}
                    onClick={() => { setShowGate({ dryRun: false }); setDeployCode(''); }}
                    title={pv.prod_configured ? '' : 'Production not wired — will execute as a dry-run'}>
                    🚀 Deploy to Production
                  </button>
                  {!pv.twofa_enabled && <span style={{ color: '#fca5a5', fontSize: 12, alignSelf: 'center' }}>Enrol 2FA first</span>}
                </div>
              </>
            )}
          </div>

          {/* ── Live deploy log ─────────────────────────────────────── */}
          {(log.length > 0 || result) && (
            <div style={C.card}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', marginBottom: 8 }}>
                Deploy log {result && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 700,
                  color: result.status === 'success' ? '#6ee7b7' : result.status === 'rolled_back' ? '#fbbf24' : '#fca5a5' }}>
                  {result.dry_run ? 'DRY-RUN · ' : ''}{result.status.toUpperCase()}</span>}
              </div>
              <pre style={{ ...C.mono, color: '#cbd5e1', background: 'rgba(2,6,23,0.7)', padding: 12, borderRadius: 8,
                maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{log.join('\n')}</pre>
            </div>
          )}

          {/* ── History ─────────────────────────────────────────────── */}
          <div style={C.card}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9', marginBottom: 10 }}>Deployment history</div>
            {(!pv.history || pv.history.length === 0) ? (
              <div style={{ color: '#64748b', fontSize: 13 }}>No deployments yet.</div>
            ) : (
              <table className="admin-table">
                <thead><tr><th>#</th><th>When</th><th>By</th><th>Target</th><th>Commits</th><th>Mode</th><th>Status</th><th>Summary</th></tr></thead>
                <tbody>
                  {pv.history.map((h: any) => (
                    <tr key={h.id}>
                      <td style={{ color: '#94a3b8' }}>{h.id}</td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{new Date(h.created_at).toLocaleString()}</td>
                      <td style={{ color: '#cbd5e1', fontSize: 12 }}>{h.actor}</td>
                      <td style={{ ...C.mono, color: '#c4b5fd', fontSize: 11 }}>{h.to_sha}</td>
                      <td style={{ color: '#94a3b8' }}>{h.commit_count}</td>
                      <td style={{ fontSize: 11, color: h.dry_run ? '#94a3b8' : '#93c5fd' }}>{h.dry_run ? 'dry-run' : 'live'}</td>
                      <td style={{ fontSize: 12, fontWeight: 700,
                        color: h.status === 'success' ? '#6ee7b7' : h.status === 'rolled_back' ? '#fbbf24' : h.status === 'running' ? '#93c5fd' : '#fca5a5' }}>
                        {h.status}
                      </td>
                      <td style={{ color: '#94a3b8', fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── 2FA gate modal ──────────────────────────────────────────── */}
      {showGate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !running && setShowGate(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0f172a', border: '1px solid rgba(139,92,246,0.35)',
            borderRadius: 12, padding: 24, width: 380 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#f1f5f9', marginBottom: 6 }}>
              {showGate.dryRun ? 'Dry run' : '🚀 Deploy to Production'}
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>
              {showGate.dryRun
                ? 'Simulate the promotion (no changes pushed). Enter your 2FA code.'
                : (pv?.prod_configured
                    ? 'This will merge develop→main and deploy to production. Enter your 2FA code to authorise.'
                    : 'Production is not wired yet, so this will run as a dry-run. Enter your 2FA code.')}
            </div>
            <input style={C.input} maxLength={6} placeholder="000000" autoFocus value={deployCode}
              onChange={e => setDeployCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => { if (e.key === 'Enter' && deployCode.length === 6 && !running) runDeploy(); }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="admin-btn primary" onClick={runDeploy} disabled={running || deployCode.length !== 6}>
                {running ? 'Working…' : (showGate.dryRun ? 'Run dry-run' : 'Authorise & Deploy')}
              </button>
              <button className="admin-btn" onClick={() => setShowGate(null)} disabled={running}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
